import type ApolloCache from '@wora/apollo-cache'
import type { DealQuery, Deal, SearchDealsQuery } from '../generated/types'
import { DealDocument, SearchDealsDocument, SearchedDealFragmentDoc } from '../generated/types'
import { FullDealFragmentDoc } from '../generated/types'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import type { CacheProxy } from '.'
import { updateQueries } from './util'

/**
 * Updates any cached deals in the apollo cache
 */
export function updateCachedDeal(proxy: CacheProxy, deal: Deal) {
  const cache = proxy as ApolloCache
  cache.writeQuery({
    query: DealDocument,
    variables: { _id: deal._id },
    data: {
      deal: {
        __typename: 'Deal',
        ...deal
      }
    }
  })

  // update all searchDeals queries to remove the deal
  if (deal._deleted) {
    updateQueries<SearchDealsQuery>(cache, {
      query: SearchDealsDocument,
      update: (data) => {
        const filteredDeals = data.searchDeals.deals.filter((q) => q._id !== deal._id)

        return {
          ...data,
          searchDeals: {
            ...data.searchDeals,
            deals: filteredDeals,
            total_rows:
              filteredDeals.length !== data.searchDeals.deals.length
                ? data.searchDeals.total_rows - 1
                : data.searchDeals.total_rows
          }
        }
      }
    })
  }

  cache.writeFragment({
    fragment: SearchedDealFragmentDoc,
    id: defaultDataIdFromObject({ id: deal._id, __typename: 'Deal' }),
    data: {
      __typename: 'Deal',
      ...deal
    }
  })

  cache.writeFragment({
    fragment: FullDealFragmentDoc,
    id: defaultDataIdFromObject({ id: deal._id, __typename: 'Deal' }),
    data: {
      __typename: 'Deal',
      ...deal
    }
  })
}

export function updateCachedDealPartially(cache: ApolloCache, dealId: string, values: Partial<Deal>) {
  try {
    const deal = cache.readQuery<DealQuery>({
      query: DealDocument,
      variables: { id: dealId }
    }).deal

    updateCachedDeal(cache, {
      ...deal,
      ...values
    })
  } catch (e) {
    console.warn('Unable to find deal for id ' + dealId)
  }
}
